/**
 * flashcards — client feature module (spec §4.1, §4.6, brief §flashcards).
 * Scheduling previews always come from `shared/sm2.ts`'s numbers via the
 * server DTO — never recomputed here.
 *
 * A single top-level path (`/flashcards`) with a nested `review` child — not
 * two top-level routes — so this feature still contributes exactly one entry
 * to `client/src/app/router.test.tsx`'s "matches every route every feature
 * registers" check (frozen: one path per feature), the same pattern
 * `practice/index.ts` and `exam/index.ts` use.
 */
import { Layers } from "lucide-react";
import type { FeatureModule } from "@client/app/types";
import { FlashcardsPage } from "./Page";
import { FlashcardsReviewPage } from "./ReviewPage";

const flashcardsFeature: FeatureModule = {
  id: "flashcards",
  routes: [
    {
      path: "/flashcards",
      children: [
        { index: true, Component: FlashcardsPage },
        { path: "review", Component: FlashcardsReviewPage },
      ],
    },
  ],
  nav: [
    {
      to: "/flashcards",
      labelKey: "flashcards.nav",
      icon: Layers,
      order: 30,
      hideDuringExam: true,
    },
  ],
  dict: {
    sv: {
      "flashcards.nav": "Kort",
      "flashcards.description":
        "Repetera begrepp och formler med utspridd repetition, blandat från alla moment.",
      "flashcards.mixed.title": "Dagens repetition · alla moment",
      "flashcards.mixed.caption":
        "En kö för alla moment. Blandad ordning känns svårare men ger bättre resultat.",
      "flashcards.mixed.start": "Starta repetitionen",
      "flashcards.decks.browse": "Bläddra per moment",
      "flashcards.deck.total.one": "{count} kort totalt",
      "flashcards.deck.total.other": "{count} kort totalt",
      "flashcards.deck.due.one": "{count} förfallet",
      "flashcards.deck.due.other": "{count} förfallna",
      "flashcards.deck.new.one": "{count} nytt",
      "flashcards.deck.new.other": "{count} nya",
      "flashcards.deck.learned.one": "{count} inlärt",
      "flashcards.deck.learned.other": "{count} inlärda",
      "flashcards.deck.reviewDue": "Repetera förfallna",
      "flashcards.deck.learnNew": "Lär nya",
      "flashcards.deck.topicFilter.label": "Ämne",
      "flashcards.deck.topicFilter.all": "Alla ämnen",
      "flashcards.deck.empty": "Inga kort i det här momentet ännu.",
      "flashcards.deck.resume": "Fortsätt repetitionen",
      "flashcards.deck.replace.title": "Ersätt pågående repetition?",
      "flashcards.deck.replace.body":
        "Du har en repetition kvar att avsluta. Om du börjar en ny ersätts den sparade repetitionen.",
      "flashcards.deck.replace.confirm": "Ersätt repetitionen",
      "flashcards.review.waiting.title": "Nästa kort är snart redo",
      "flashcards.review.waiting.body": "Repetera igen om {time}.",
      "flashcards.review.waiting.backToDecks": "Till kortlekarna",
      "flashcards.stats.title": "Statistik",
      "flashcards.stats.streak": "Repeterat i rad",
      "flashcards.stats.streak.days.one": "{count} dag",
      "flashcards.stats.streak.days.other": "{count} dagar",
      "flashcards.stats.retention": "Andel rätt (ej igen)",
      "flashcards.stats.reviewsPerDay": "Repetitioner senaste 30 dagarna",
      "flashcards.stats.maturity.title": "Kortens mognad",
      "flashcards.stats.maturity.new": "Nya",
      "flashcards.stats.maturity.learning": "Under inlärning",
      "flashcards.stats.maturity.young": "Unga",
      "flashcards.stats.maturity.mature": "Mogna",
      "flashcards.stats.noData": "Ingen statistik ännu — börja repetera!",
      "flashcards.review.pageTitle": "Repetera kort",
      "flashcards.review.pageTitleChapter": "Repetera kort · moment {chapter}",
      "flashcards.review.pageTitleMixed": "Repetera kort · alla moment",
      "flashcards.review.remaining": "{count} kvar",
      "flashcards.review.progress": "{done} av {total} kort",
      "flashcards.review.recall.label": "Ditt svar först",
      "flashcards.review.recall.help":
        "Skriv gärna några ord innan du tittar. Det behöver inte vara exakt.",
      "flashcards.review.recall.yours": "Du skrev",
      "flashcards.review.dontKnow": "Jag vet inte",
      "flashcards.review.flipHint":
        "Välj hur säker du är (1–3) för att visa baksidan. Att skriva först hjälper minnet.",
      "flashcards.review.chapters": "Moment {chapters}",
      "flashcards.review.queue.title": "Nästa kort",
      "flashcards.review.cardHeading": "Kort",
      "flashcards.review.backHeading": "Baksida",
      "flashcards.review.rating.again": "Igen",
      "flashcards.review.rating.hard": "Svårt",
      "flashcards.review.rating.good": "Bra",
      "flashcards.review.rating.easy": "Lätt",
      "flashcards.review.previewDays": "{count} d",
      "flashcards.review.previewMinutes": "{count} min",
      "flashcards.review.kind.concept": "Begrepp",
      "flashcards.review.kind.formula": "Formel",
      "flashcards.review.kind.contrast": "Jämförelse",
      "flashcards.review.kind.boundary": "Giltighet",
      "flashcards.review.empty.title": "Inget att repetera just nu",
      "flashcards.topic.opens": "Ämnets kort öppnas {date}, när föreläsningens frågor öppnas.",
      "flashcards.topic.closed": "Stängd tills vidare. Läraren har inte öppnat ämnet än.",
      "flashcards.review.empty.body": "Du har inga förfallna eller nya kort i det här urvalet.",
      "flashcards.review.summary.title": "Sammanfattning",
      "flashcards.review.summary.reviewed.one": "{count} kort repeterat",
      "flashcards.review.summary.reviewed.other": "{count} kort repeterade",
      "flashcards.review.summary.againRate": '{percent} fick "igen"',
      "flashcards.review.summary.nextDue": "Nästa repetition: {date}",
      "flashcards.review.summary.noneDue": "Inget mer att repetera just nu.",
      "flashcards.review.summary.backToDecks": "Tillbaka till korten",
      "flashcards.review.summary.backToToday": "Tillbaka till dagens pass",
    },
    en: {
      "flashcards.nav": "Flashcards",
      "flashcards.description":
        "Review concepts and formulas with spaced repetition, mixed across all modules.",
      "flashcards.mixed.title": "Today's review · all modules",
      "flashcards.mixed.caption":
        "One queue for all modules. Mixed order feels harder and works better.",
      "flashcards.mixed.start": "Start the review",
      "flashcards.decks.browse": "Browse by module",
      "flashcards.deck.total.one": "{count} card total",
      "flashcards.deck.total.other": "{count} cards total",
      "flashcards.deck.due.one": "{count} due",
      "flashcards.deck.due.other": "{count} due",
      "flashcards.deck.new.one": "{count} new",
      "flashcards.deck.new.other": "{count} new",
      "flashcards.deck.learned.one": "{count} learned",
      "flashcards.deck.learned.other": "{count} learned",
      "flashcards.deck.reviewDue": "Review due",
      "flashcards.deck.learnNew": "Learn new",
      "flashcards.deck.topicFilter.label": "Topic",
      "flashcards.deck.topicFilter.all": "All topics",
      "flashcards.deck.empty": "No cards in this module yet.",
      "flashcards.deck.resume": "Resume review",
      "flashcards.deck.replace.title": "Replace the unfinished review?",
      "flashcards.deck.replace.body":
        "You still have a review to finish. Starting a new one replaces the saved review.",
      "flashcards.deck.replace.confirm": "Replace review",
      "flashcards.review.waiting.title": "The next card will be ready soon",
      "flashcards.review.waiting.body": "Review again in {time}.",
      "flashcards.review.waiting.backToDecks": "Back to decks",
      "flashcards.stats.title": "Stats",
      "flashcards.stats.streak": "Streak",
      "flashcards.stats.streak.days.one": "{count} day",
      "flashcards.stats.streak.days.other": "{count} days",
      "flashcards.stats.retention": "Retention (not again)",
      "flashcards.stats.reviewsPerDay": "Reviews, last 30 days",
      "flashcards.stats.maturity.title": "Card maturity",
      "flashcards.stats.maturity.new": "New",
      "flashcards.stats.maturity.learning": "Learning",
      "flashcards.stats.maturity.young": "Young",
      "flashcards.stats.maturity.mature": "Mature",
      "flashcards.stats.noData": "No stats yet — start reviewing!",
      "flashcards.review.pageTitle": "Review cards",
      "flashcards.review.pageTitleChapter": "Review cards · module {chapter}",
      "flashcards.review.pageTitleMixed": "Review cards · all modules",
      "flashcards.review.remaining": "{count} left",
      "flashcards.review.progress": "{done} of {total} cards",
      "flashcards.review.recall.label": "Your answer first",
      "flashcards.review.recall.help":
        "If you can, type a few words before you look. It does not need to be exact.",
      "flashcards.review.recall.yours": "You wrote",
      "flashcards.review.dontKnow": "I don't know",
      "flashcards.review.flipHint":
        "Choose how sure you are (1–3) to show the back. Typing first helps you remember.",
      "flashcards.review.chapters": "Module {chapters}",
      "flashcards.review.queue.title": "Next cards",
      "flashcards.review.cardHeading": "Card",
      "flashcards.review.backHeading": "Back",
      "flashcards.review.rating.again": "Again",
      "flashcards.review.rating.hard": "Hard",
      "flashcards.review.rating.good": "Good",
      "flashcards.review.rating.easy": "Easy",
      "flashcards.review.previewDays": "{count} d",
      "flashcards.review.previewMinutes": "{count} min",
      "flashcards.review.kind.concept": "Concept",
      "flashcards.review.kind.formula": "Formula",
      "flashcards.review.kind.contrast": "Contrast",
      "flashcards.review.kind.boundary": "Validity",
      "flashcards.review.empty.title": "Nothing to review right now",
      "flashcards.topic.opens":
        "This topic's cards open {date}, when the lecture's questions open.",
      "flashcards.topic.closed":
        "Closed until further notice. The teacher has not opened this topic yet.",
      "flashcards.review.empty.body": "You have no due or new cards in this selection.",
      "flashcards.review.summary.title": "Summary",
      "flashcards.review.summary.reviewed.one": "{count} card reviewed",
      "flashcards.review.summary.reviewed.other": "{count} cards reviewed",
      "flashcards.review.summary.againRate": '{percent} rated "again"',
      "flashcards.review.summary.nextDue": "Next review: {date}",
      "flashcards.review.summary.noneDue": "Nothing else to review right now.",
      "flashcards.review.summary.backToDecks": "Back to the cards",
      "flashcards.review.summary.backToToday": "Back to today's session",
    },
  },
};

export default flashcardsFeature;
